import {
  APP_INITIALIZER,
  LOCALE_ID,
  NgModule,
  Optional,
  PLATFORM_ID,
  provideZoneChangeDetection,
} from '@angular/core';

import {
  ViewportScroller,
  isPlatformBrowser,
  registerLocaleData,
} from '@angular/common';
import localeIt from '@angular/common/locales/it';

import { RouteReuseStrategy, Router, Scroll } from '@angular/router';
import { CoreModule } from '@core/core.module';
import { CacheRouteReuseStrategy } from '@core/route-reuse.strategy';
import { AnalyticsDirective } from '@core/services/analytics.directive';
import { ApiService } from '@core/services/api.service';
import * as Sentry from '@sentry/angular-ivy';
import { LanguageService } from '@shared/services/language.service';
import { TranslationLoaderService } from '@shared/services/translation.service';
import { BlockUIModule } from 'ng-block-ui';
import { BlockUIHttpModule } from 'ng-block-ui/http';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha';
import { filter } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';


registerLocaleData(localeIt);

import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
} from '@angular/common/http';
import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { FooterComponent } from '@core/components/footer/footer.component';
import { HeaderMobileComponent } from '@core/components/header-mobile/header-mobile.component';
import { HeaderComponent } from '@core/components/header/header.component';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import Aura from '@primeng/themes/aura';
import { CookieInterceptor } from '@shared/interceptors/cookie.interceptor';
import { SSR_ACCEPT_LANGUAGE, SSR_PATH } from '@shared/tokens/server.token';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { providePrimeNG } from 'primeng/config';
import { catchError, firstValueFrom, of } from 'rxjs';

export function getLanguage(languageService: LanguageService) {
  if (!languageService.getWindow()) {
    return 'it';
  }
  return languageService.getWindow().navigator.language == 'it'
    ? languageService.getWindow().navigator.language
    : 'en';
}

export function preloadTranslationsFactory(
  translate: TranslateService,
  languageService: LanguageService,
  platformId: object,
  ssrPath: string | null,
  ssrLang: string | null
) {
  return async () => {
    const pathLang =
      ssrLang ??
      ssrPath?.split('/')[1] ??
      (isPlatformBrowser(platformId)
        ? globalThis.location?.pathname?.split('/')[1]
        : undefined);

    const langFromPath = pathLang === 'it' || pathLang === 'en' ? pathLang : '';
    const defaultLanguage = langFromPath || 'en';

    let langCode = defaultLanguage;
    let langId = defaultLanguage === 'it' ? 1 : 2;

    if (isPlatformBrowser(platformId)) {
      const savedLangCode = localStorage.getItem('langCode');
      const savedLangId = Number.parseInt(localStorage.getItem('langId') ?? '', 10);
      const hasSavedLanguage = !!savedLangCode && !Number.isNaN(savedLangId);

      if (hasSavedLanguage) {
        if (langFromPath && savedLangCode !== defaultLanguage) {
          langCode = defaultLanguage;
          langId = defaultLanguage === 'it' ? 1 : 2;
          localStorage.setItem('langCode', langCode);
          localStorage.setItem('langId', String(langId));
        } else {
          langCode = savedLangCode as 'it' | 'en';
          langId = savedLangId;
        }
      } else {
        const browserLang = globalThis.navigator?.language?.toLowerCase() ?? 'en';
        if (defaultLanguage === 'it' || (!langFromPath && browserLang.startsWith('it'))) {
          langCode = 'it';
          langId = 1;
        }

        localStorage.setItem('langCode', langCode);
        localStorage.setItem('langId', String(langId));
      }
    }

    languageService.userSelectedLanguage = {
      idLanguage: langId,
      code: langCode,
      description: '',
    };

    if (translate.currentLang !== langCode) {
      translate.setDefaultLang(langCode);
      await firstValueFrom(translate.use(langCode).pipe(catchError(() => of({}))));
    }
  };
}

@NgModule({
  declarations: [AppComponent, AnalyticsDirective],
  imports: [
    BrowserModule.withServerTransition({ appId: 'memorabid' }),
    AppRoutingModule,
    CoreModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslationLoaderService,
        deps: [ApiService],
      },
    }),
    BlockUIModule.forRoot({
      delayStart: 210,
      delayStop: 10,
    }),
    BlockUIHttpModule.forRoot({
      requestFilters: [
        /geteventnotification|starredarticle|filter\/getalltypes|getcompetitions|getteams|getseasons|validate-shipping|getalltypes|getallv2|getallpublicv2|homePageCms(?:\/public)?|gethistory|geteventdateend|getauctionpriceinfo|getcountunreadmessages|get-current-prices|get-suggested|gettotalamount|user\/ping|user\/getbyusername|memorabidevent\/getbypk\/|memorabidevent\/getbyslug/,
      ],
    }),
    HeaderComponent,
    HeaderMobileComponent,
    FooterComponent,
    // ServiceWorkerModule.register('ngsw-worker.js', {
    //   enabled: true,
    //   // Register the ServiceWorker as soon as the application is stable
    //   // or after 30 seconds (whichever comes first).
    //   registrationStrategy: 'registerWhenStable:30000',
    //   scope: '/',
    // }),
    ServiceWorkerModule.register('custom-sw.js', {
      enabled: true,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
      scope: '/',
    }),
  ],
  exports: [AnalyticsDirective],
  providers: [
    {
      provide: LOCALE_ID,
      //useValue: "it-IT"
      deps: [LanguageService],
      useFactory: getLanguage,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: RouteReuseStrategy,
      useClass: CacheRouteReuseStrategy,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: preloadTranslationsFactory,
      deps: [
        TranslateService,
        LanguageService,
        PLATFORM_ID,
        [new Optional(), SSR_PATH],
        [new Optional(), SSR_ACCEPT_LANGUAGE],
      ],
      multi: true,
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptcha.siteKey,
    },
    { provide: HTTP_INTERCEPTORS, useClass: CookieInterceptor, multi: true },
    provideClientHydration(
      withEventReplay(),
      withHttpTransferCacheOptions({
        includeRequestsWithAuthHeaders: true,
        filter: (a) => {
          const cachedUrls = [
            'genericpage',
            'publicgeneric',
            'entitydata/getbasedatabypk',
            'tag/getbyslug',
            'api/filter',
            'api/Translation/getalllanguages',
            'translation/getall',
            'auction/getdefaultdata',
            'auctionView/get-suggested',
            'api/categories/related/by-tag-and-seller',
            'filter/getalltypes',
            'campaign/getbypk',
            'homePageCms',
            'getallpublicv2',
            'memorabidevent',
          ];

          if (a.method === 'GET') {
            const cached = cachedUrls.some((url) =>
              a.url.toLowerCase().includes(url.toLowerCase())
            );
            return cached;
          }
          return false;
        },
      })
    ),
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    provideEnvironmentNgxMask(),
    provideHttpClient(withFetch()),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          dark: false,
          darkModeSelector: false,
        },
      },
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private _router: Router, _viewportScroller: ViewportScroller) {
    this._router.events
      .pipe(filter((event) => event instanceof Scroll))
      .subscribe((event) => {
        const e = event as Scroll;
        if (e.anchor) {
          // anchor navigation
          /* setTimeout is the core line to solve the solution */
          setTimeout(() => {
            _viewportScroller.scrollToAnchor(e.anchor!);
          });
        } else if (e.position) {
          // backward navigation
          _viewportScroller.scrollToPosition(e.position);
        } else {
          // forward navigation
          _viewportScroller.scrollToPosition([0, 0]);
        }
      });
  }
}
